import { toDateString, dayjs } from '@koob/dates';
import { BenefitKind, decodeId } from 'utils';
import {
  BenefitsKindEnum,
  PromotionKindEnum,
  PromotionStateEnum,
  PromotionStatusEnum,
} from '@koob/enums';

export const createPromotionPeriodFactory = ({
  startAt = undefined,
  endAt = undefined,
  days = {
    Mo: false,
    Tu: false,
    We: false,
    Th: false,
    Fr: false,
    Sa: false,
    Su: false,
  },
  autoConfirmEnabled = null,
} = {}) => ({
  startAt,
  endAt,
  days,
  autoConfirmEnabled,
});

export const mapApiResponseToFormValues = ({
  id,
  startAt,
  endAt,
  availableDays,
  days,
  ageRanges,
  paxRanges,
  bookingPeriods,
}) => {
  const Days = {
    Monday: 'Mo',
    Tuesday: 'Tu',
    Wednesday: 'We',
    Thursday: 'Th',
    Friday: 'Fr',
    Saturday: 'Sa',
    Sunday: 'Su',
  };
  return {
    id,
    startAt:
      startAt && dayjs(startAt).isValid() ? toDateString(startAt) : undefined,
    endAt:
      endAt && dayjs(endAt).isValid()
        ? toDateString(dayjs(endAt).subtract(1, 'day'))
        : undefined,
    days: Object.keys(Days).reduce((acc, curr) => {
      acc[Days[curr]] = ((availableDays ?? days) || []).includes(curr);
      return acc;
    }, {}),
    ageRanges: (ageRanges ?? []).map((range) => {
      return {
        ageRange: [range.ageRange[0], range.ageRange[1]],
        price: range.value,
      };
    }),
    paxRanges: (paxRanges ?? []).map((range) => {
      return {
        paxRange: [range.paxRange[0], range.paxRange[1]],
        price: range.value,
      };
    }),
    bookingPeriods: (bookingPeriods ?? []).map((promoBookingPeriod) => ({
      id: promoBookingPeriod?.id,
      startAt:
        promoBookingPeriod?.startAt &&
        dayjs(promoBookingPeriod?.startAt).isValid()
          ? toDateString(promoBookingPeriod?.startAt)
          : undefined,
      endAt:
        promoBookingPeriod?.endAt && dayjs(promoBookingPeriod?.endAt).isValid()
          ? toDateString(dayjs(promoBookingPeriod?.endAt).subtract(1, 'day'))
          : undefined,
      days: Object.keys(Days).reduce((acc, curr) => {
        acc[Days[curr]] = (promoBookingPeriod?.days || []).includes(curr);
        return acc;
      }, {}),
    })),
  };
};

export const formatHotelPayloadForPromoCreation = (hotelPayload, hotelId) => {
  const getDaysArray = (daysObj) => {
    const dayMap = {
      Mo: 1,
      Tu: 2,
      We: 3,
      Th: 4,
      Fr: 5,
      Sa: 6,
      Su: 7,
    };
    return Object.entries(daysObj || {})
      .filter(([, isActive]) => isActive)
      .map(([day]) => dayMap[day]);
  };

  const isStayFlexibilityDeal =
    hotelPayload.kind === PromotionKindEnum.STAY_FLEXIBILITY;
  const isSupplementPromotionType = [
    'supplement',
    'supplement_percent',
  ].includes(hotelPayload.params?.type?.value);

  return {
    name: hotelPayload.name,
    state: hotelPayload.state,
    kind: hotelPayload.kind,
    appliesToDmc: hotelPayload.appliesToDmc,
    minNumberOfRooms: hotelPayload.minRooms,
    numberOfNights: hotelPayload.numberOfNights,
    conditionalDaysCount: hotelPayload?.conditionalDaysCount,
    // KD-3586: each linked promotion carries its rank, and the promotion its
    // own; the latter only means something within a cumulation.
    linkedPromotions: (hotelPayload.cumulativePromotions ?? []).map(
      ({ id, name, calculationOrder }) => ({
        id,
        name,
        calculationOrder: calculationOrder ?? null,
      }),
    ),
    cumulationCalculationOrder:
      hotelPayload.cumulativePromotions?.length > 0
        ? (hotelPayload.cumulationCalculationOrder ?? null)
        : null,
    organizations: hotelPayload.organizations,
    mandatorySupplement: hotelPayload.mandatorySupplement,
    customCancelationPolicies: Boolean(hotelPayload.customCancelationPolicies),
    cancelationPolicies: hotelPayload.cancelationPolicies,
    hotelId: decodeId(hotelId).toString(),
    benefit: {
      kind: getBenefitKindKey(hotelPayload.params?.type?.value),
      value:
        hotelPayload.params?.value !== undefined &&
        hotelPayload.params?.value !== null
          ? hotelPayload.params.value
          : null,
      promoPerRoom: hotelPayload.params?.promoPerRoom,
      unit: hotelPayload.params?.unit,
      includeExtraBeds: hotelPayload.params?.includeExtraBeds,
      extraBedsOnly: hotelPayload.params?.extraBedsOnly ?? false,
      applicableRooms: hotelPayload.params?.applicableRooms,
      benefitAgeGroups: hotelPayload.params?.benefitAgeGroups ?? [],
      benefitAmenities: hotelPayload.params?.amenities ?? [],
      code:
        isStayFlexibilityDeal && isSupplementPromotionType
          ? (hotelPayload.stayFlexibleCode ?? null)
          : null,
      upgradedFromRoom: hotelPayload.params?.upgradedRoom?.from
        ? {
            displayName: hotelPayload.params?.upgradedRoom?.from?.label,
            id: hotelPayload.params?.upgradedRoom?.from?.value,
          }
        : null,
      upgradedToRoom: hotelPayload.params?.upgradedRoom?.to
        ? {
            displayName: hotelPayload.params?.upgradedRoom?.to?.label,
            id: hotelPayload.params?.upgradedRoom?.to?.value,
          }
        : null,
    },
    availablePeriods: (hotelPayload.periods || []).map((period) => ({
      id: period.id,
      startAt: period.startAt,
      endAt: period.endAt,
      days: getDaysArray(period.days),
      // Skip rows left blank in the form: drafts bypass API validation.
      // No id: the API recreates the booking periods on every save.
      bookingPeriods: (period.bookingPeriods ?? [])
        .filter((bp) => bp.startAt && bp.endAt)
        .map((bp) => ({
          startAt: bp.startAt,
          endAt: bp.endAt,
          days: getDaysArray(bp.days),
        })),
    })),
    minPax: hotelPayload.params?.paxRestriction?.minPax || undefined,
    maxPax: hotelPayload.params?.paxRestriction?.maxPax || undefined,
  };
};

export const getBenefitKindKey = (value) => {
  return Object.entries(BenefitKind).find(([, v]) => v === value)?.[0] || null;
};

export const mapUpgradedRoomApiToFormValue = (item) => {
  if (item) {
    return {
      label: `${item?.displayName} (${item?.count})`,
      value: item?.id,
    };
  }
};

export const PromotionAppliesToEnum = {
  DMC: 'dmc',
  TOS: 'tos',
  DMC_AND_TOS: 'dmcAndTos',
  NOT_SET: 'notSet',
};

// An offer applies to the DMC and/or the TOs it lists.
export const getPromotionAppliesTo = ({ appliesToDmc, appliedToCount }) => {
  const appliesToTos = appliedToCount > 0;
  if (appliesToDmc && appliesToTos) return PromotionAppliesToEnum.DMC_AND_TOS;
  if (appliesToDmc) return PromotionAppliesToEnum.DMC;
  if (appliesToTos) return PromotionAppliesToEnum.TOS;
  return PromotionAppliesToEnum.NOT_SET;
};

// KD-3666 — badges of the promotions list "Status" column. The lifecycle stage
// (draft / archived) wins over the date-based status the API computes for
// operational offers; completeness is a second badge on drafts only.
export const getPromotionStatusBadges = ({ state, isComplete, status }) => {
  if (state === PromotionStateEnum.DRAFT) {
    return isComplete
      ? [PromotionStatusEnum.DRAFT]
      : [PromotionStatusEnum.DRAFT, PromotionStatusEnum.INCOMPLETE];
  }
  if (state === PromotionStateEnum.ARCHIVED) {
    return [PromotionStatusEnum.ARCHIVED];
  }
  const dateBased = [
    PromotionStatusEnum.ACTIVE,
    PromotionStatusEnum.PLANNED,
    PromotionStatusEnum.EXPIRED,
  ];
  return dateBased.includes(status) ? [status] : [];
};

// KD-3750 — the commercial family of the promotion being edited, as the API's
// promotionFamilyOf classifies it. Saved promotions carry theirs in
// `exclusiveFamily`; only the unsaved form has to be classified here.
// KD-3833 — read from the kind and the benefit only, never from the name.
export const getPromotionExclusiveFamily = ({ kind, benefitKind }) => {
  if (
    [BenefitsKindEnum.SUPPLEMENT, BenefitsKindEnum.SUPPLEMENT_PERCENT].includes(
      benefitKind,
    )
  ) {
    return null;
  }
  // A free night wins over the kind: an early booker giving a bonus night is a
  // Stay/Pay offer and stays cumulative with an Early Bird discount.
  if (benefitKind === BenefitsKindEnum.FREE_NIGHT) {
    return 'stay_pay';
  }
  if (kind === PromotionKindEnum.EARLY_BOOKER) {
    return 'early_bird';
  }
  return null;
};
